import { useCallback } from "react";
import { useZero } from "@rocicorp/zero/react";
import { toast } from "sonner";
import { queries } from "@/lib/queries";
import { mutators } from "@/lib/mutators";
import { useCurrentUser } from "@/lib/current-user";
import {
  trustChannel,
  waitForChannelInByUser,
} from "@/lib/channel-navigation-trust";

/**
 * Hook that returns a handler for opening (or creating) a DM with another user.
 * Deduplicates the find-or-create-DM pattern used across many components.
 * DMs are workspace-independent (universal).
 */
export function useOpenDM(onSelect: ((channelId: string) => void) | undefined) {
  const zero = useZero();
  const { user } = useCurrentUser();

  return useCallback(
    async (userId: string) => {
      if (!user?.id || userId === user.id) return;

      try {
        // Check for an existing DM (workspace-independent)
        const dms = await zero.run(queries.channels.dmsByUserWithMembers());
        const existing = dms.find((ch) => {
          const ids = new Set(ch.members?.map((m) => m.user_id) ?? []);
          return ch.type === "dm" && ids.size === 2 && ids.has(userId);
        });
        if (existing) {
          await waitForChannelInByUser(zero, existing.id);
          trustChannel(existing.id);
          onSelect?.(existing.id);
          return;
        }

        // Create a new DM
        await zero.mutate(
          mutators.actions.getOrCreateDirectDM({
            actorUserId: user.id,
            otherUserId: userId,
          }),
        );

        // Retry: Zero sync may not have delivered the new channel yet
        const findDm = async () => {
          const refreshed = await zero.run(
            queries.channels.dmsByUserWithMembers(),
          );
          return refreshed.find((ch) => {
            const ids = new Set(ch.members?.map((m) => m.user_id) ?? []);
            return ch.type === "dm" && ids.size === 2 && ids.has(userId);
          });
        };
        let newDm: Awaited<ReturnType<typeof findDm>> | undefined;
        for (let attempt = 0; attempt < 5 && !newDm; attempt++) {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 200));
          try {
            newDm = await findDm();
          } catch {
            // Zero query failed — retry
          }
        }
        if (newDm) {
          await waitForChannelInByUser(zero, newDm.id);
          trustChannel(newDm.id);
          onSelect?.(newDm.id);
        } else {
          // Mutator succeeded but sync hasn't delivered the row after 1s of
          // retries. Tell the user something tangible — silent failure on
          // a deliberate click is the worst outcome.
          toast.error("Couldn't open the conversation. Try again.");
        }
      } catch (err) {
        // Surface the failure instead of swallowing it as an unhandled
        // promise rejection. The consumer (`actions.openForUser`) calls
        // this with `void`, so without this catch the user sees nothing.
        const msg =
          err instanceof Error ? err.message : "Couldn't open conversation.";
        toast.error(msg);
      }
    },
    [user, zero, onSelect],
  );
}
